import { ExclamationCircleIcon } from '@heroicons/react/solid';
import { Button, ButtonVariantEnum, Modal } from '@Wonder-Cave/ui';
import { IBulkDeactivateReviewSummary } from './DeactivateNumbersBulkModal';

interface IDeactivateNumbersBulkReviewModalProps {
  show: boolean;
  isLoading: boolean;
  summary: IBulkDeactivateReviewSummary | null;
  onCancel: () => void;
  onConfirm: () => void;
}

interface IReviewGroup {
  count: number;
  areaCode: string;
  tcrCampaignLabel: string;
  providerName: string;
}

const DeactivateNumbersBulkReviewModal = ({
  show,
  isLoading,
  summary,
  onCancel,
  onConfirm,
}: IDeactivateNumbersBulkReviewModalProps) => {
  if (!summary) {
    return null;
  }

  const groupMap = new Map<string, IReviewGroup>();
  let totalNumbersToDeactivate = 0;
  const campaignsWithIncluded = new Set<string>();
  for (const c of summary.eligibleCampaigns) {
    for (const p of c.phoneNumbers) {
      if (p.exclusionReason !== null) continue;
      const areaCode = p.phoneNumber.slice(0, 3);
      const providerName = p.providerName ?? 'N/A';
      const key = `${c.tcrCampaignLabel}__${areaCode}__${providerName}`;
      const existing = groupMap.get(key);
      if (existing) {
        existing.count += 1;
      } else {
        groupMap.set(key, { count: 1, areaCode, tcrCampaignLabel: c.tcrCampaignLabel, providerName });
      }
      totalNumbersToDeactivate += 1;
      campaignsWithIncluded.add(c.tcrCampaignLabel);
    }
  }

  const groups = Array.from(groupMap.values()).sort((a, b) => {
    if (a.tcrCampaignLabel !== b.tcrCampaignLabel) return a.tcrCampaignLabel.localeCompare(b.tcrCampaignLabel);
    if (a.areaCode !== b.areaCode) return a.areaCode.localeCompare(b.areaCode);
    return a.providerName.localeCompare(b.providerName);
  });

  const eligibleCount = campaignsWithIncluded.size;
  const showCriteriaError = summary.selectedTcrCount !== null && eligibleCount < summary.selectedTcrCount;
  const confirmDisabled = isLoading || totalNumbersToDeactivate === 0;

  const titleComponent = (
    <div className='flex flex-col mt-4 mb-4'>
      <h2 className='w-full'>Review Deactivation</h2>
      <div className='pt-2 text-regular font-medium text-dark-gray'>
        {totalNumbersToDeactivate} {totalNumbersToDeactivate === 1 ? 'number' : 'numbers'} will be deactivated:
      </div>
    </div>
  );

  const issueText = `Only ${eligibleCount} out of ${summary.selectedTcrCount} selected TCR campaigns have numbers matching the criteria.`;

  return (
    <Modal
      title={titleComponent}
      show={show}
      onClose={onCancel}
      titleClassName='pl-20 text-black'
      childrenClassName='modal-panel-grey pl-20 pr-20 pb-2 pt-2'
    >
      <div className='flex flex-col w-full'>
        <div className='grid grid-cols-[2fr_2fr_3fr_3fr] gap-x-4 pb-3 border-b border-mystic text-h5 font-medium tracking-widest uppercase text-medium-gray'>
          <span>Quantity</span>
          <span>Area Code</span>
          <span>TCR Campaign ID</span>
          <span>Provider</span>
        </div>

        <div className='flex flex-col max-h-96 overflow-y-auto'>
          {groups.length === 0 ? (
            <div className='py-6 text-center text-sm text-medium-gray'>
              No numbers eligible for deactivation.
            </div>
          ) : (
            groups.map((group, idx) => (
              <div
                key={`${group.tcrCampaignLabel}-${group.areaCode}-${group.providerName}-${idx}`}
                className='grid grid-cols-[2fr_2fr_3fr_3fr] gap-x-4 items-center py-3 border-b border-mystic text-sm text-dark-gray'
              >
                <span>{group.count}</span>
                <span>{group.areaCode}</span>
                <span>{group.tcrCampaignLabel}</span>
                <span>{group.providerName}</span>
              </div>
            ))
          )}
        </div>

        <div className='flex flex-row items-center justify-between pt-6 pb-4 gap-4'>
          {showCriteriaError ? (
            <span className='flex items-center gap-2 text-small font-bold text-black'>
              <ExclamationCircleIcon className='w-3 h-3 text-flamingo' />
              {issueText}
            </span>
          ) : <span />}
          <Button
            variant={ButtonVariantEnum.PRIMARY}
            onClick={onConfirm}
            type='button'
            isLoading={isLoading}
            disabled={confirmDisabled}
          >
            CONFIRM DEACTIVATION
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default DeactivateNumbersBulkReviewModal;
